'use client'

import React from 'react'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import ProductGallery from '@/components/ui/ProductGallery'
import { getLinkHref } from '@/components/Link'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { FeaturedProductsBlock } from '@/payload-types'

// Extract types from payload-types
type ProductItem = FeaturedProductsBlock['products'][0]
type MoreLinkConfig = FeaturedProductsBlock['moreLink']
type SettingsConfig = FeaturedProductsBlock['settings']

interface FeaturedProductsClientProps {
  tag: string
  title: string
  subtitle: string
  products: ProductItem[]
  moreLink?: MoreLinkConfig
  settings?: SettingsConfig
}

const FeaturedProductsClient: React.FC<FeaturedProductsClientProps> = ({
  tag,
  title,
  subtitle,
  products,
  moreLink,
  settings,
}) => {
  const normalizedSettings = normalizeSectionSettings(settings)

  const galleryProducts = products.map((product, index) => {
    const link = product.link
    const ref = link?.type === 'reference' ? link.reference : null
    const refValue = ref && typeof ref.value === 'object' ? (ref.value as { slug?: string }) : null
    return {
      id: product.id || String(index),
      title: product.title,
      shortDescription: product.shortDescription,
      image: product.image,
      href: getLinkHref(link) || '#',
      slug: refValue?.slug,
    }
  })

  return (
    <SectionWrapper settings={normalizedSettings}>
      <SectionHeader infoTagText={tag} title={title} subtitle={subtitle} className="max-w-4xl" />

      <div className="flex flex-col gap-6">
        <ProductGallery products={galleryProducts} sourceBlock="featuredProducts" />

        {moreLink && (
          <div className="flex items-center gap-1 mt-6">
            <LinkWithIcon link={moreLink} variant="link" color="brand" size="md" />
          </div>
        )}
      </div>
    </SectionWrapper>
  )
}

export default FeaturedProductsClient
